import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CampaignChannel } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { communicationsApi } from '@/services/communications.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Composes and sends a message to a fixed set of people — one profile's
 * "send a message" action, or a People-list selection.
 *
 * Creates a `PERSON_IDS` campaign and sends it in the same action,
 * skipping the DRAFT-then-confirm screen a broadcast campaign needs —
 * the audience here is already exactly who the operator meant, not
 * something that benefits from a review step. `CampaignService.send()`
 * itself is untouched: this is the same two calls
 * `campaign-form-page.tsx` makes, just without stopping in between.
 */
export function SendMessageDialog({
  onClose,
  onSent,
  recipientIds,
  recipientLabel,
}: {
  onClose: () => void;
  onSent: () => void;
  readonly recipientIds: readonly string[];
  /** A name for one person, or a count for a selection — shown so the
   *  operator can confirm who they are about to message. */
  readonly recipientLabel: string;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [channel, setChannel] = useState<CampaignChannel>('EMAIL');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      const campaign = await communicationsApi.create({
        channel,
        subject: channel === 'EMAIL' ? subject.trim() : null,
        body: body.trim(),
        audienceKind: 'PERSON_IDS',
        audienceTagIds: [],
        audiencePersonIds: [...recipientIds],
        audienceGroupId: null,
      });
      return communicationsApi.send(campaign.id);
    },
    onSuccess: onSent,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  const canSubmit = body.trim().length > 0 && (channel !== 'EMAIL' || subject.trim().length > 0);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="send-message-title"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="send-message-title">
          {t('communications.sendMessage.titleFor', { values: { recipient: recipientLabel } })}
        </h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="field">
          <label className="field-label" htmlFor="send-message-channel">
            {t('communications.campaign.channel')}
          </label>
          <select
            id="send-message-channel"
            className="field-input"
            value={channel}
            onChange={(event) => setChannel(event.target.value as CampaignChannel)}
          >
            <option value="EMAIL">{t('communications.campaign.channelLabel.EMAIL')}</option>
            <option value="SMS">{t('communications.campaign.channelLabel.SMS')}</option>
          </select>
          {/* Visibly present, not omitted — the port exists but is never
              bound to a real push vendor, so offering it as a live option
              would fail on send. */}
          <label className="checkbox" aria-disabled="true">
            <input type="checkbox" disabled />
            <span>{t('communications.campaign.channelLabel.PUSH')}</span>
          </label>
        </div>

        {channel === 'EMAIL' && (
          <Field
            label={t('communications.campaign.subject')}
            name="subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            required
          />
        )}

        <div className="field">
          <label className="field-label" htmlFor="send-message-body">
            {t('communications.campaign.body')}
          </label>
          <textarea
            id="send-message-body"
            className="field-input"
            rows={6}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            required
          />
        </div>

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            {t('communications.sendMessage.send')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
